import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { BsLinkedin, BsPersonPlusFill } from "react-icons/bs";
import { ArrowRight, BellRing, Check, ChevronLeft, ChevronRight, Copy, Info, ListPlus, Loader2, MessageSquareText, Plus, Send, X } from "lucide-react";
import {
    FunnelRate, LinkedinTemplate, Outcome, Overview, ProspectFilters, ProspectRow, ProspectState, Scope, SearchResult, SignalKey, addExternalInvites,
    getLinkedinTemplates, getOverview, invite, setMemo, searchProspects, sendQueued, setFollowUp, setOutcome, syncToCrm,
} from "../../interfaces/linkedinProspecting";
import { createProspect, getProspectTags, updateProspect } from "../../interfaces/prospect";
import { CRM_COLUMNS, crmColumnIndex, crmStatusFor, crmSuggestion } from "../../lib/linkedinCrm";
import { FRENCH_DEPARTMENTS, FRENCH_REGIONS, REGION_TO_DEPARTMENTS } from "../../lib/frenchCities";
import { NB_ETAB_BUCKETS } from "../../lib/nbEtabBuckets";
import { Button } from "../../components/ui/Button";
import InviteDialog from "./InviteDialog";
import { ADD_PILL, AddMenu, Avatar, ICON_BUTTON, MemoField, Chip, FollowUpMenu, ProspectMeta, ScoreCircle, SectionTitle, SignalTag, TagMenu } from "./parts";
import { SIGNAL_FILTERS, dueLabel, firstName, momentFor, momentOf, relativeFr } from "./vocabulary";
import MessagesManager from "./MessagesManager";
import MessageDialog from "./MessageDialog";
import { Me } from "./variables";
import { getMyInfo } from "../../interfaces/user";
import { PageHeader } from "../../components/ui/PageHeader";
import { Skeleton } from "../../components/ui/Skeleton";


const TABS: Array<{ key: Scope; label: string }> = [
    { key: "discover", label: "Découvrir" },
    { key: "invited", label: "Invitations envoyées" },
    { key: "accepted", label: "Invitation acceptée" },
    { key: "messaged", label: "Message envoyé" },
    { key: "replied", label: "A répondu" },
    { key: "meeting", label: "RDV pris" },
    { key: "signed", label: "Signé" },
    { key: "followup", label: "À relancer" },
];

/** Mirrors the backend's `inScope`: the furthest stage reached, plus À relancer while a follow-up is noted. */
function inScope(state: ProspectState, scope: Scope): boolean {
    return scope === "followup" ? !!state.followUpAt : stageOf(state) === scope;
}

function stageOf(state: ProspectState): Exclude<Scope, "followup"> {
    if (state.invite && state.customer) return "signed";
    if (state.invite?.meetingAt) return "meeting";
    if (state.invite?.repliedAt) return "replied";
    if (state.invite?.messagedAt) return "messaged";
    if (state.invite?.acceptedAt) return "accepted";
    if (state.invite) return "invited";
    return "discover";
}

/** The filter bar's own shape — regions are kept by name and only expanded for the API. */
interface UiFilters {
    signals: SignalKey[];
    regions: string[];
    departments: string[];
    nbEtab: string[];
}

const EMPTY_FILTERS: UiFilters = { signals: [], regions: [], departments: [], nbEtab: [] };

const toApiFilters = (f: UiFilters): ProspectFilters => ({
    signals: f.signals,
    departments: [...new Set([...f.regions.flatMap((r) => REGION_TO_DEPARTMENTS[r] || []), ...f.departments])],
    nbEtab: f.nbEtab,
});

const activeCount = (f: UiFilters) => f.signals.length + f.regions.length + f.departments.length + f.nbEtab.length;

const toggled = <T,>(list: T[], value: T, on: boolean): T[] => (on ? [...list, value] : list.filter((v) => v !== value));

const plural = (n: number, word: string) => `${n} ${word}${n > 1 ? "s" : ""}`;

const fold = (text: string) => text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

// ─── KPIs ─────────────────────────────────────────────────────────────────────

function KpiStrip({ overview }: { overview: Overview | null }) {
    const quota = overview?.quota;
    const rates = overview?.rates;
    const pct = quota ? Math.min(100, Math.round((quota.sentToday / quota.limit) * 100)) : 0;
    const full = !!quota && quota.sentToday >= quota.limit;
    const rate = (value: number | null | undefined) =>
        value == null ? <span className="text-ink-faint">—</span> : <>{value}<span className="text-base font-normal text-ink-faint"> %</span></>;

    // Each step over the one before it: where the funnel leaks shows on that step only.
    const cells: Array<{ label: string; step?: FunnelRate; base: (n: number) => string; help: string }> = [
        { label: "Acceptation", step: rates?.acceptance, base: (n) => `des ${plural(n, "invitation")}`, help: "Invitations acceptées sur les invitations envoyées." },
        { label: "Réponse", step: rates?.reply, base: (n) => `des ${plural(n, "message")} envoyés`, help: "Prospects qui ont répondu, sur ceux à qui vous avez écrit." },
        { label: "RDV", step: rates?.meeting, base: (n) => `des ${plural(n, "réponse")}`, help: "Rendez-vous pris, sur les prospects qui ont répondu." },
        { label: "Signature", step: rates?.signature, base: (n) => `des ${n} RDV`, help: "Signatures, sur les prospects rencontrés." },
    ];

    return (
        <div className="grid grid-cols-2 rounded-2xl border border-border bg-surface md:grid-cols-5 md:divide-x md:divide-border">
            <div className="col-span-2 px-5 py-4 md:col-span-1">
                <div className="flex items-center gap-1.5">
                    <p className="font-sans text-xs text-ink-muted">Invitations aujourd'hui</p>
                    <button
                        type="button"
                        title="Pour préserver votre compte LinkedIn, nous recommandons de ne pas envoyer plus de 20 invitations par jour."
                        aria-label="Conseil LinkedIn : ne pas envoyer plus de 20 invitations par jour pour préserver votre compte."
                        className="text-ink-faint transition-colors hover:text-ink-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                    >
                        <Info className="h-3.5 w-3.5" />
                    </button>
                </div>
                {overview ? (
                    <p className="font-sans text-2xl font-semibold tracking-tight text-ink">
                        {quota ? quota.sentToday : "—"}
                        {quota && <span className="text-base font-normal text-ink-faint"> / {quota.limit}</span>}
                    </p>
                ) : (
                    <div className="flex h-8 items-center"><Skeleton className="h-6 w-16" /></div>
                )}
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-surface-sunken">
                    <div className={`h-full rounded-full transition-all ${full ? "bg-warning" : "bg-success"}`} style={{ width: `${pct}%` }} />
                </div>
                {full && <p className="mt-1 font-sans text-xs text-warning">Limite atteinte : la suite part demain</p>}
            </div>
            {cells.map((c) => (
                <div key={c.label} title={c.help} className="border-t border-border px-5 py-4 md:border-t-0">
                    <p className="font-sans text-xs text-ink-muted">{c.label}</p>
                    {overview ? (
                        <>
                            <p className="font-sans text-2xl font-semibold tracking-tight text-ink">{rate(c.step?.rate)}</p>
                            <p className="font-sans text-xs text-ink-faint">{c.step ? `${c.step.n} ${c.base(c.step.base)}` : ""}</p>
                        </>
                    ) : (
                        <>
                            <div className="flex h-8 items-center"><Skeleton className="h-6 w-14" /></div>
                            <div className="flex h-4 items-center"><Skeleton className="h-3 w-28" /></div>
                        </>
                    )}
                    {c.label === "Signature" && rates?.overall.rate != null && (
                        <p className="font-sans text-xs text-ink-faint">soit {rates.overall.rate} % des invitations</p>
                    )}
                </div>
            ))}
        </div>
    );
}

// ─── Filters (Découvrir) ──────────────────────────────────────────────────────

const field =
    "h-8 w-full rounded-lg border-0 bg-surface px-2.5 font-sans text-xs text-ink placeholder:text-ink-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";

function DepartmentPicker({ selected, onChange }: { selected: string[]; onChange: (codes: string[]) => void }) {
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const q = fold(query);
    const options = FRENCH_DEPARTMENTS.filter(
        (d) => !selected.includes(d.code) && (!q || d.code.toLowerCase().startsWith(q) || fold(d.name).includes(q))
    ).slice(0, 8);

    return (
        <div className="space-y-1.5">
            <div className="relative">
                <input
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                    onBlur={() => setTimeout(() => setOpen(false), 120)}
                    placeholder="Ajouter un département"
                    className={field}
                />
                {open && options.length > 0 && (
                    <ul className="absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-y-auto rounded-2xl border border-border bg-surface p-1.5 shadow-float">
                        {options.map((d) => (
                            <li key={d.code}>
                                <button
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => {
                                        onChange([...selected, d.code]);
                                        setQuery("");
                                    }}
                                    className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left font-sans text-xs text-ink hover:bg-surface-sunken"
                                >
                                    <span className="w-6 font-mono text-ink-faint">{d.code}</span>
                                    {d.name}
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            {selected.length > 0 && (
                <div className="flex flex-wrap gap-1">
                    {selected.map((code) => (
                        <span key={code} className="inline-flex items-center gap-1 rounded-full bg-ink py-0.5 pl-2.5 pr-1 font-sans text-xs text-ink-inverse">
                            {code} · {FRENCH_DEPARTMENTS.find((d) => d.code === code)?.name || code}
                            <button
                                onClick={() => onChange(selected.filter((c) => c !== code))}
                                className="rounded-full p-0.5 hover:bg-ink-inverse/20"
                                aria-label="Retirer"
                            >
                                <X className="h-3 w-3" />
                            </button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

/** Sober, on the side: small chips for the short lists, a select and a picker for geography. */
function SideFilters({ filters, setFilters }: { filters: UiFilters; setFilters: (f: UiFilters) => void }) {
    return (
        <div className="space-y-5">
            <div>
                <SectionTitle>Nb établissements</SectionTitle>
                <div className="flex flex-wrap gap-1">
                    {NB_ETAB_BUCKETS.map(({ value, label, sub }) => {
                        const on = filters.nbEtab.includes(value);
                        return (
                            <Chip key={value} active={on} onClick={() => setFilters({ ...filters, nbEtab: toggled(filters.nbEtab, value, !on) })}>
                                {label}
                                <span className={`ml-1 ${on ? "text-ink-inverse/70" : "text-ink-faint"}`}>{sub}</span>
                            </Chip>
                        );
                    })}
                </div>
            </div>

            <div>
                <SectionTitle>Signaux</SectionTitle>
                <div className="flex flex-wrap gap-1">
                    {SIGNAL_FILTERS.map((sig) => {
                        const on = filters.signals.includes(sig.key);
                        return (
                            <Chip key={sig.key} active={on} onClick={() => setFilters({ ...filters, signals: on ? [] : [sig.key] })}>
                                {sig.label}
                            </Chip>
                        );
                    })}
                </div>
            </div>

            <div>
                <SectionTitle>Région</SectionTitle>
                <select
                    value={filters.regions[0] || ""}
                    onChange={(e) => setFilters({ ...filters, regions: e.target.value ? [e.target.value] : [] })}
                    className={`${field} ${filters.regions.length ? "" : "text-ink-faint"}`}
                >
                    <option value="">Toutes les régions</option>
                    {FRENCH_REGIONS.map((r) => (
                        <option key={r} value={r}>
                            {r}
                        </option>
                    ))}
                </select>
            </div>

            <div>
                <SectionTitle>Département</SectionTitle>
                <DepartmentPicker selected={filters.departments} onChange={(departments) => setFilters({ ...filters, departments })} />
            </div>

            {activeCount(filters) > 0 && (
                <button
                    onClick={() => setFilters(EMPTY_FILTERS)}
                    className="rounded font-sans text-xs text-ink-muted underline-offset-2 hover:text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                    Effacer les filtres
                </button>
            )}
        </div>
    );
}

// ─── External invitations ─────────────────────────────────────────────────────

function ExternalInvitesDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const count = text.split(/[,;\s]+/).filter((c) => /linkedin\.com\/in\//i.test(c)).length;

    useEffect(() => {
        const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
        document.addEventListener("keydown", esc);
        return () => document.removeEventListener("keydown", esc);
    }, [onClose]);

    const submit = async () => {
        setBusy(true);
        try {
            const res = await addExternalInvites(text);
            const recorded = res.added + res.matched;
            const parts = [
                recorded ? `${plural(recorded, "invitation")} ajoutée${recorded > 1 ? "s" : ""}` : "Aucune invitation ajoutée",
                res.already ? `${res.already} déjà dans la liste` : "",
                res.invalid.length ? `${plural(res.invalid.length, "lien")} non reconnu${res.invalid.length > 1 ? "s" : ""}` : "",
            ].filter(Boolean);
            toast[recorded ? "success" : "info"](parts.join(" · ") + ".");
            onDone();
            onClose();
        } catch {
            toast.error("Les invitations n'ont pas pu être enregistrées.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 sm:items-center sm:p-4" onMouseDown={onClose}>
            <div
                role="dialog"
                aria-modal="true"
                aria-label="Invitations envoyées hors page"
                onMouseDown={(e) => e.stopPropagation()}
                className="w-full max-w-lg rounded-t-2xl bg-surface shadow-float sm:rounded-2xl"
            >
                <div className="flex items-start gap-3 px-5 pb-3 pt-5">
                    <div className="min-w-0 flex-1">
                        <h2 className="font-sans text-lg font-semibold text-ink">Invitation externe</h2>
                        <p className="font-sans text-sm text-ink-muted">
                            Collez un ou plusieurs liens de profil LinkedIn, séparés par une virgule. Le nom est lu dans le lien.
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="rounded-lg p-1.5 text-ink-faint hover:bg-surface-sunken hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                        aria-label="Fermer"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <div className="px-5 pb-4">
                    <textarea
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        rows={5}
                        autoFocus
                        placeholder="https://www.linkedin.com/in/jean-dupont, https://www.linkedin.com/in/marie-martin"
                        className="w-full resize-none rounded-lg border-0 bg-surface-sunken p-3 font-sans text-sm text-ink placeholder:text-ink-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                    />
                </div>
                <div className="flex items-center gap-2 border-t border-border px-5 py-3">
                    <span className="font-sans text-xs text-ink-faint">{count > 0 ? plural(count, "lien") : ""}</span>
                    <div className="ml-auto flex gap-2">
                        <Button size="sm" variant="ghost" onClick={onClose}>
                            Annuler
                        </Button>
                        <Button size="sm" disabled={busy || count === 0} onClick={submit}>
                            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                            Ajouter
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── Rows ─────────────────────────────────────────────────────────────────────

/** A recorded outcome as a toggle: green and ticked once done. */
/** One stage as a toggle pill: green with a check once reached. */
function Step({ done, label, onClick, disabled }: { done: boolean; label: string; onClick: () => void; disabled?: boolean }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-pressed={done}
            className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 font-sans text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-40 ${
                done ? "border-success/30 bg-success-subtle text-success" : "border-border text-ink-muted hover:border-border-strong hover:text-ink"
            }`}
        >
            {done && <Check className="h-3 w-3" />}
            {label}
        </button>
    );
}

/** A small status pill next to the name: what needs looking at, at a glance. */
function StatusPill({ tone, children }: { tone: "danger" | "accent" | "neutral" | "success"; children: React.ReactNode }) {
    const tones = {
        danger: "bg-danger-subtle text-danger",
        accent: "bg-accent-subtle text-accent",
        neutral: "bg-surface-sunken text-ink-muted",
        success: "bg-success-subtle text-success",
    };
    return <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 font-sans text-xs font-medium ${tones[tone]}`}>{children}</span>;
}

const LINKEDIN_PRIMARY = "!h-8 !rounded-full !bg-[#0a66c2] !px-3.5 !text-sm !font-semibold !text-white hover:!bg-[#004182]";

interface RowHandlers {
    open: (row: ProspectRow) => void;
    followUp: (row: ProspectRow, iso: string | null) => void;
    outcome: (row: ProspectRow, outcome: Outcome) => void;
    sendQueued: (row: ProspectRow) => void;
    addToCrm: (row: ProspectRow) => void;
    memo: (row: ProspectRow, text: string) => Promise<void>;
    copyNote: (row: ProspectRow) => void;
    /** Opens the message dialog — once connected. */
    write: (row: ProspectRow) => void;
    /** On the CRM card: its tags, its kanban column. */
    tags: (row: ProspectRow, tags: string[]) => void;
    moveCrm: (row: ProspectRow, status: string) => void;
}

function ProspectListRow({
    row, scope, checked, onCheck, busy, allTags, h,
}: { row: ProspectRow; scope: Scope; checked: boolean; onCheck: (v: boolean) => void; busy: boolean; allTags: string[]; h: RowHandlers }) {
    const inv = row.state.invite;
    const due = row.state.followUpAt ? dueLabel(row.state.followUpAt) : null;
    const move = row.crm && crmSuggestion(stageOf(row.state), row.crm.status);
    const canWrite = scope !== "discover" && !!momentFor(row.state);
    const pipeline = scope !== "discover";

    return (
        <li onClick={() => h.open(row)} className="group/row flex cursor-pointer items-start gap-3 px-4 py-4 transition-colors hover:bg-surface-sunken/50">
            <input
                type="checkbox"
                checked={checked}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => onCheck(e.target.checked)}
                className="mt-2.5 h-4 w-4 shrink-0 rounded-sm border-border-strong accent-ink"
                aria-label={`Sélectionner ${row.name}`}
            />
            <Avatar name={row.name} />

            <div className="min-w-0 flex-1 space-y-1.5">
                {/* Who, and what needs looking at */}
                <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="truncate font-sans text-sm font-semibold text-ink">{row.name}</span>
                    <a
                        href={row.linkedinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="shrink-0 rounded text-ink-faint hover:text-info focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                        aria-label="Profil LinkedIn"
                    >
                        <BsLinkedin className="h-3.5 w-3.5" />
                    </a>
                    {row.state.customer && <StatusPill tone="success">Client</StatusPill>}
                    {due && (
                        <StatusPill tone={due.overdue ? "danger" : due.today ? "accent" : "neutral"}>
                            <BellRing className="h-3 w-3" />
                            {due.overdue ? due.text : `Relance ${due.text.toLowerCase()}`}
                        </StatusPill>
                    )}
                    {inv?.status === "queued" && (
                        <StatusPill tone="neutral">
                            Programmée{inv.queuedFor ? ` le ${new Date(inv.queuedFor).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}` : ""}
                        </StatusPill>
                    )}
                </div>
                <ProspectMeta age={row.age} portfolio={row.portfolio} external={row.external} name={row.name} />

                {scope === "discover" && row.signals[0] && <SignalTag signal={row.signals[0]} />}

                {/* Where they are — on every one of the rep's tables, À relancer included */}
                {pipeline && inv?.status === "sent" && (
                    <div className="flex flex-wrap items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                        <span className="mr-1 font-sans text-xs text-ink-faint">Envoyée {inv.sentAt ? relativeFr(inv.sentAt) : ""}</span>
                        <Step done={!!inv.acceptedAt} label="Acceptée" disabled={busy} onClick={() => h.outcome(row, { accepted: !inv.acceptedAt })} />
                        <Step done={!!inv.messagedAt} label="Message envoyé" disabled={busy} onClick={() => h.outcome(row, { messaged: !inv.messagedAt })} />
                        <Step done={!!inv.repliedAt} label="A répondu" disabled={busy} onClick={() => h.outcome(row, { replied: !inv.repliedAt })} />
                        <Step done={!!inv.meetingAt} label="RDV pris" disabled={busy} onClick={() => h.outcome(row, { meeting: !inv.meetingAt })} />
                        <Step done={row.state.customer} label="Signé" disabled={busy} onClick={() => h.outcome(row, { signed: !row.state.customer })} />
                    </div>
                )}

                {/* The rep's working layer, quieter: the CRM move LinkedIn calls for, tags, note */}
                <div className="flex flex-wrap items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                    {move && (
                        <button
                            type="button"
                            disabled={busy}
                            onClick={() => h.moveCrm(row, move.status)}
                            title="Déplacer la carte du CRM selon l'étape LinkedIn"
                            className="inline-flex items-center gap-1 rounded-full bg-accent-subtle px-2 py-0.5 font-sans text-xs font-medium text-accent transition-colors hover:bg-accent hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-40"
                        >
                            <ArrowRight className="h-3 w-3" />
                            {move.label}
                        </button>
                    )}
                    <TagMenu tags={row.crm?.tags || []} allTags={allTags} inCrm={!!row.crm} disabled={busy} onChange={(tags) => h.tags(row, tags)} />
                    {pipeline && <MemoField memo={row.state.memo} disabled={busy} onSave={(text) => h.memo(row, text)} />}
                    {pipeline && inv?.note && !inv.acceptedAt && (
                        <button type="button" onClick={() => h.copyNote(row)} className={ADD_PILL}>
                            <Copy className="h-3 w-3" />
                            Copier l'invitation
                        </button>
                    )}
                </div>
            </div>

            {/* Secondary actions as round icons, then the one primary action */}
            <div className="flex shrink-0 items-center gap-1.5 pt-0.5" onClick={(e) => e.stopPropagation()}>
                <AddMenu establishmentId={row.establishment._id || null} crmProspectId={row.crm?.id || null} onCrm={() => h.addToCrm(row)} disabled={busy} />
                <FollowUpMenu
                    iconOnly
                    active={!!due}
                    label={due ? "Reporter la relance" : "Programmer une relance"}
                    disabled={busy}
                    onPick={(iso) => h.followUp(row, iso)}
                />
                {scope === "followup" && (
                    <button type="button" disabled={busy} onClick={() => h.followUp(row, null)} title="Relance faite" aria-label="Relance faite" className={ICON_BUTTON}>
                        <Check className="h-4 w-4" />
                    </button>
                )}

                {scope === "discover" ? (
                    <Button size="sm" disabled={busy} onClick={() => h.open(row)} className={LINKEDIN_PRIMARY}>
                        <BsPersonPlusFill className="h-4 w-4" />
                        Se connecter
                    </Button>
                ) : inv?.status === "queued" ? (
                    <Button size="sm" disabled={busy} onClick={() => h.sendQueued(row)} className={LINKEDIN_PRIMARY}>
                        <BsPersonPlusFill className="h-4 w-4" />
                        Envoyer
                    </Button>
                ) : canWrite ? (
                    // LinkedIn's own « Message » button.
                    <Button size="sm" disabled={busy} onClick={() => h.write(row)} className={LINKEDIN_PRIMARY}>
                        <Send className="h-4 w-4" />
                        Message
                    </Button>
                ) : null}
            </div>
            {/* An external invitation has nothing to score; keep the column so rows stay aligned. */}
            {row.external ? <div className="h-10 w-10 shrink-0" /> : <ScoreCircle score={row.score} factors={row.factors} />}
        </li>
    );
}

const SKELETON_NAME = ["w-36", "w-28", "w-44", "w-32", "w-40", "w-28", "w-36", "w-32"];
const SKELETON_META = ["w-56", "w-48", "w-64", "w-44", "w-60", "w-52", "w-48", "w-56"];
const STEP_WIDTHS = ["w-20", "w-28", "w-24", "w-20", "w-14"];

/** `ProspectListRow` while the table loads: the same columns at the same sizes, per table. */
function ProspectRowsSkeleton({ scope }: { scope: Scope }) {
    const pipeline = scope !== "discover";
    return (
        <ul className="divide-y divide-border" aria-busy="true" aria-label="Chargement des prospects">
            {SKELETON_NAME.map((nameW, i) => (
                <li key={i} className="flex items-start gap-3 px-4 py-4">
                    <div className="mt-2.5 h-4 w-4 shrink-0 rounded-sm border border-border-strong" />
                    <Skeleton className="h-10 w-10 shrink-0 rounded-full" />

                    <div className="min-w-0 flex-1 space-y-1.5">
                        <div className="flex h-5 items-center gap-2">
                            <Skeleton className={`h-3.5 ${nameW}`} />
                            <Skeleton className="h-3.5 w-3.5" />
                        </div>
                        <div className="flex h-4 items-center">
                            <Skeleton className={`h-3 ${SKELETON_META[i]}`} />
                        </div>
                        {pipeline ? (
                            <div className="flex flex-wrap items-center gap-1.5">
                                <Skeleton className="mr-1 h-3 w-24" />
                                {STEP_WIDTHS.map((w, k) => (
                                    <Skeleton key={k} className={`h-6 rounded-full ${w}`} />
                                ))}
                            </div>
                        ) : (
                            i % 3 !== 2 && (
                                <div className="flex h-4 items-center">
                                    <Skeleton className="h-3 w-40" />
                                </div>
                            )
                        )}
                        <div className="flex items-center gap-1.5">
                            <div className="h-5 w-14 rounded-full border border-dashed border-border-strong" />
                            {pipeline && <div className="h-5 w-16 rounded-full border border-dashed border-border-strong" />}
                        </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-1.5 pt-0.5">
                        <Skeleton className="h-8 w-8 rounded-full" />
                        <Skeleton className="h-8 w-8 rounded-full" />
                        {(!pipeline || i % 2 === 0) && <Skeleton className={`h-8 rounded-full ${pipeline ? "w-24" : "w-32"}`} />}
                    </div>
                    {pipeline && i % 4 === 3 ? <div className="h-10 w-10 shrink-0" /> : <Skeleton className="h-10 w-10 shrink-0 rounded-full" />}
                </li>
            ))}
        </ul>
    );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const EMPTY_TEXT: Record<Scope, string> = {
    discover: "Aucun prospect ne correspond à ces filtres.",
    invited: "Aucune invitation en attente.",
    accepted: "Aucune invitation acceptée en attente de message.",
    messaged: "Aucun message en attente de réponse.",
    replied: "Aucune réponse pour l'instant.",
    meeting: "Aucun RDV pris pour l'instant.",
    signed: "Aucune signature pour l'instant.",
    followup: "Rien à relancer.",
};

export default function LinkedinProspecting() {
    const [tab, setTab] = useState<Scope>("discover");
    const [filters, setFiltersState] = useState<UiFilters>(EMPTY_FILTERS);
    const [page, setPage] = useState(1);
    const [result, setResult] = useState<SearchResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [checked, setChecked] = useState<Set<string>>(new Set());
    const [busyId, setBusyId] = useState<string | null>(null);
    const [overview, setOverview] = useState<Overview | null>(null);
    const [openId, setOpenId] = useState<string | null>(null);
    const [externalOpen, setExternalOpen] = useState(false);
    const [tagFilter, setTagFilterState] = useState("");
    const [allTags, setAllTags] = useState<string[]>([]);
    const [offer, setOffer] = useState("");
    const [templates, setTemplates] = useState<LinkedinTemplate[]>([]);
    const [manager, setManager] = useState<"offer" | "templates" | null>(null);
    const [messageRow, setMessageRow] = useState<ProspectRow | null>(null);
    /** The rep's own name, company and phone, for the {mon …} variables. */
    const [meInfo, setMeInfo] = useState<Omit<Me, "offer">>({});
    const me = useMemo<Me>(() => ({ ...meInfo, offer }), [meInfo, offer]);
    const requestRef = useRef(0);

    const setFilters = (next: UiFilters) => {
        setFiltersState(next);
        setPage(1);
    };

    const load = useCallback(async () => {
        const id = ++requestRef.current;
        setLoading(true);
        setError(null);
        try {
            // Filters only show — and so only apply — in Découvrir; the rep's own tables are always whole.
            const data = await searchProspects(
                { ...(tab === "discover" ? toApiFilters(filters) : {}), scope: tab, tag: tagFilter || undefined },
                page
            );
            // Only the latest request may land — filters change faster than a scan answers.
            if (id === requestRef.current) setResult(data);
        } catch {
            if (id === requestRef.current) setError("La recherche a échoué. Réessayez dans un instant.");
        } finally {
            if (id === requestRef.current) setLoading(false);
        }
    }, [filters, tab, page, tagFilter]);

    useEffect(() => {
        load();
    }, [load]);

    const refreshOverview = useCallback(() => {
        getOverview().then(setOverview).catch(() => {});
    }, []);

    useEffect(() => {
        refreshOverview();
        // The {votre offre} variable of the invitation notes, set in Mon compte.
        getMyInfo()
            .then((res) => {
                const u = res?.data || {};
                setOffer(u.prospectingOffer || "");
                setMeInfo({ givenName: u.givenName, company: u.company, phone: u.phone });
            })
            .catch(() => {});
        // The first call seeds the default LinkedIn templates.
        getLinkedinTemplates().then(setTemplates).catch(() => {});
        getProspectTags().then(setAllTags).catch(() => {});
    }, [refreshOverview]);

    /**
     * Applies a prospect's new state to the list: it stays (patched) if it still belongs in this
     * table, and leaves it otherwise — an invited prospect must disappear from Découvrir at once.
     */
    const applyState = (personId: string, state: ProspectState) => {
        setResult((r) => {
            if (!r) return r;
            if (inScope(state, tab)) return { ...r, rows: r.rows.map((row) => (row.personId === personId ? { ...row, state } : row)) };
            return { ...r, rows: r.rows.filter((row) => row.personId !== personId), total: Math.max(0, r.total - 1) };
        });
        refreshOverview();
    };

    const copyAndOpen = async (note: string, url: string, sentToday: number, limit: number) => {
        try {
            await navigator.clipboard.writeText(note);
        } catch {
            /* clipboard refused: the note is still in the dialog */
        }
        window.open(url, "_blank", "noopener,noreferrer");
        toast.success(`Invitation envoyée · message copié, collez-le sur LinkedIn (${sentToday}/${limit} aujourd'hui).`);
    };

    /** Copies a message and offers the profile in the toast — never opens a tab on its own. */
    const copyMessage = async (note: string, url: string, text: string) => {
        try {
            await navigator.clipboard.writeText(note);
        } catch {
            toast.error("Le presse-papiers est bloqué par le navigateur.");
            return;
        }
        toast.success(
            <span>
                {text}{" "}
                <a href={url} target="_blank" rel="noopener noreferrer" className="font-medium underline">
                    Ouvrir le profil
                </a>
            </span>
        );
    };

    /**
     * The message is copied and the profile opened — LinkedIn sends it, never us. A first message
     * ticks « Message envoyé »; a follow-up picked in the dialog replaces the current one.
     */
    const onMessageSent = async (row: ProspectRow, moment: string, text: string, followUpAt: string | null) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch {
            toast.error("Le presse-papiers est bloqué par le navigateur.");
            throw new Error("clipboard");
        }
        window.open(row.linkedinUrl, "_blank", "noopener,noreferrer");
        try {
            let state = row.state;
            if (moment === "first_message" && !state.invite?.messagedAt) state = await setOutcome(row.personId, { messaged: true });
            if (followUpAt) state = await setFollowUp(row.personId, followUpAt);
            applyState(row.personId, state);
            toast.success(
                `Message copié, collez-le sur LinkedIn.${followUpAt ? ` Relance ${dueLabel(followUpAt).text.toLowerCase()}.` : ""}`
            );
        } catch {
            toast.error("Message copié, mais le suivi n'a pas pu être enregistré.");
        }
    };

    const onInvite = async (personId: string, note: string, linkedinUrl: string): Promise<boolean> => {
        try {
            const res = await invite(personId, note);
            if (res.state.invite?.status === "sent") await copyAndOpen(note, linkedinUrl, res.sentToday, res.limit);
            else toast.info("Limite du jour atteinte : invitation programmée pour demain.");
            applyState(personId, res.state);
            return true;
        } catch (err: any) {
            const already = err?.response?.data?.error === "ALREADY_INVITED";
            toast[already ? "info" : "error"](already ? "Ce prospect a déjà été invité." : "L'invitation n'a pas pu être enregistrée.");
            return false;
        }
    };

    const setTagFilter = (tag: string) => {
        setTagFilterState(tag);
        setPage(1);
    };

    /** Patches a row's CRM card in place — the LinkedIn state doesn't move. */
    const patchCrm = (personId: string, crm: ProspectRow["crm"]) =>
        setResult((r) => r && { ...r, rows: r.rows.map((row) => (row.personId === personId ? { ...row, crm } : row)) });

    /** Same LightCRM record the signal table creates, tagged as coming from LinkedIn. */
    const addCrmCard = async (row: ProspectRow, tags: string[]) => {
        const given = firstName(row.name);
        const created = await createProspect({
            name: row.name,
            firstName: given,
            lastName: row.name.replace(new RegExp(given, "i"), "").trim(),
            company: row.establishment.name || undefined,
            linkedinUrl: row.linkedinUrl,
            location: row.portfolio.cities[0] || undefined,
            establishmentId: row.establishment._id || undefined,
            source: "linkedin",
            // Straight into the column its LinkedIn stage stands for.
            status: crmStatusFor(stageOf(row.state)),
            tags,
        });
        if (!created?._id) throw new Error("not created");
        patchCrm(row.personId, { id: String(created._id), status: created.status, tags });
        rememberTags(tags);
        toast.success(
            <span>
                {row.name} ajouté au CRM{tags.length ? ` avec le tag « ${tags[0]} »` : ""} ·{" "}
                <a href={`/kanban?openProspect=${created._id}`} target="_blank" rel="noopener noreferrer" className="font-medium underline">
                    Voir dans mon CRM
                </a>
            </span>
        );
    };

    const rememberTags = (tags: string[]) => setAllTags((all) => Array.from(new Set([...all, ...tags])).sort((a, b) => a.localeCompare(b)));

    const withBusy = async (personId: string, fn: () => Promise<void>) => {
        setBusyId(personId);
        try {
            await fn();
        } catch {
            toast.error("L'action n'a pas pu être enregistrée.");
        } finally {
            setBusyId(null);
        }
    };

    const handlers: RowHandlers = {
        open: (row) => setOpenId(row.personId),
        followUp: (row, iso) =>
            withBusy(row.personId, async () => {
                applyState(row.personId, await setFollowUp(row.personId, iso));
                toast.success(iso ? `À relancer : ${dueLabel(iso).text.toLowerCase()}.` : "Relance faite.");
            }),
        outcome: (row, outcome) =>
            withBusy(row.personId, async () => {
                const state = await setOutcome(row.personId, outcome);
                applyState(row.personId, state);
                // Accepted is the moment to write: LinkedIn only lets you once connected, so the
                // first message opens right away.
                if (outcome.accepted === true && !row.state.invite?.messagedAt) setMessageRow({ ...row, state });
            }),
        write: (row) => setMessageRow(row),
        memo: async (row, text) => {
            try {
                applyState(row.personId, await setMemo(row.personId, text));
            } catch {
                toast.error("La note n'a pas pu être enregistrée.");
                throw new Error("memo not saved");
            }
        },
        copyNote: (row) => {
            if (row.state.invite?.note) copyMessage(row.state.invite.note, row.linkedinUrl, "Message copié.");
        },
        sendQueued: (row) =>
            withBusy(row.personId, async () => {
                try {
                    const res = await sendQueued(row.personId);
                    await copyAndOpen(res.note, row.linkedinUrl, res.sentToday, res.limit);
                    applyState(row.personId, res.state);
                } catch (err: any) {
                    if (err?.response?.status === 429) toast.info("Limite du jour atteinte : réessayez demain.");
                    else throw err;
                }
            }),
        addToCrm: (row) => withBusy(row.personId, () => addCrmCard(row, [])),
        tags: (row, tags) =>
            withBusy(row.personId, async () => {
                // The first tag on a prospect not in the CRM yet creates its card: the tags live there.
                if (!row.crm) return addCrmCard(row, tags);
                const updated = await updateProspect(row.crm.id, { tags });
                if (!updated?._id) throw new Error("not saved");
                patchCrm(row.personId, { ...row.crm, tags });
                rememberTags(tags);
            }),
        moveCrm: (row, status) =>
            withBusy(row.personId, async () => {
                if (!row.crm) return;
                const updated = await updateProspect(row.crm.id, { status });
                if (!updated?._id) throw new Error("not saved");
                patchCrm(row.personId, { ...row.crm, status });
                toast.success(`${row.name} passé en « ${CRM_COLUMNS[crmColumnIndex(status)].label} » dans le CRM.`);
            }),
    };

    const rows = result?.rows || [];
    const checkedRows = rows.filter((r) => checked.has(r.personId));

    const switchTab = (next: Scope) => {
        setTab(next);
        setPage(1);
        setChecked(new Set());
        setResult(null);
    };

    // Découvrir shows no count on purpose: the size of the LinkedIn base is not the rep's business.
    const count = (scope: Scope) => (scope === "discover" ? undefined : overview?.counts[scope]);

    return (
        <div className="min-h-screen bg-canvas font-sans">
            <div className="mx-auto max-w-screen-2xl space-y-6 px-4 pb-6 md:px-8">
                <PageHeader
                    icon={<BsLinkedin />}
                    title="LinkedIn"
                    subtitle="Invitations, messages et relances auprès des gérants"
                    className="!mb-0"
                    actions={
                        <Button variant="ghost" size="sm" onClick={() => setManager("templates")}>
                            <MessageSquareText className="h-4 w-4" />
                            Messages
                        </Button>
                    }
                />

                <KpiStrip overview={overview} />

                {/* ── Tabs ── */}
                <div className="flex gap-6 overflow-x-auto border-b border-border">
                    {TABS.map((t) => {
                        const n = count(t.key);
                        const due = t.key === "followup" && !!overview?.counts.followupDue;
                        return (
                            <button
                                key={t.key}
                                onClick={() => switchTab(t.key)}
                                className={`-mb-px inline-flex items-center gap-2 whitespace-nowrap border-b-2 py-2.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                                    tab === t.key ? "border-ink font-medium text-ink" : "border-transparent text-ink-muted hover:text-ink"
                                }`}
                            >
                                {t.label}
                                {n !== undefined && (
                                    <span
                                        className={`rounded-full px-2 font-mono text-xs leading-5 ${due ? "bg-accent text-white" : "bg-surface-sunken text-ink-muted"}`}
                                        title={due ? `${overview!.counts.followupDue} à relancer aujourd'hui` : undefined}
                                    >
                                        {n}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
                    {tab === "discover" && (
                        <aside className="lg:col-span-3 xl:col-span-2">
                            <SideFilters filters={filters} setFilters={setFilters} />
                        </aside>
                    )}

                    <div className={`min-w-0 ${tab === "discover" ? "lg:col-span-9 xl:col-span-10" : "lg:col-span-12"}`}>
                        <div className="overflow-hidden rounded-2xl border border-border bg-surface">
                            {/* Count, or the bulk bar once something is selected */}
                            <div className="flex h-12 items-center gap-3 border-b border-border px-4">
                                <input
                                    type="checkbox"
                                    checked={rows.length > 0 && checkedRows.length === rows.length}
                                    onChange={(e) => setChecked(e.target.checked ? new Set(rows.map((r) => r.personId)) : new Set())}
                                    className="h-4 w-4 shrink-0 rounded-sm border-border-strong accent-ink"
                                    aria-label="Tout sélectionner"
                                />
                                {checkedRows.length > 0 ? (
                                    <>
                                        <span className="font-sans text-sm text-ink">{plural(checkedRows.length, "sélectionné")}</span>
                                        <Button
                                            size="sm"
                                            variant="secondary"
                                            className="ml-auto"
                                            onClick={async () => {
                                                try {
                                                    const res = await syncToCrm(checkedRows.filter((r) => !r.external).map((r) => r.personId));
                                                    toast.success(`${plural(res.establishments, "établissement")} ajouté${res.establishments > 1 ? "s" : ""} à « ${res.listName} ».`);
                                                    setChecked(new Set());
                                                } catch {
                                                    toast.error("L'ajout à Mes listes a échoué.");
                                                }
                                            }}
                                        >
                                            <ListPlus className="h-4 w-4" />
                                            Ajouter à Mes listes
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <span className="font-sans text-xs text-ink-muted">
                                            {loading ? (
                                        tab !== "discover" && <Skeleton className="inline-block h-3 w-20 align-middle" />
                                    ) : (
                                        result && tab !== "discover" && plural(result.total, "prospect")
                                    )}
                                        </span>
                                        {allTags.length > 0 && (
                                            <select
                                                value={tagFilter}
                                                onChange={(e) => setTagFilter(e.target.value)}
                                                aria-label="Filtrer par tag CRM"
                                                className={`ml-auto h-8 rounded-lg border bg-surface px-2 font-sans text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                                                    tagFilter ? "border-ink text-ink" : "border-border text-ink-muted"
                                                }`}
                                            >
                                                <option value="">Tous les tags CRM</option>
                                                {allTags.map((t) => (
                                                    <option key={t} value={t}>
                                                        {t}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                        {tab === "invited" && (
                                            <button
                                                onClick={() => setExternalOpen(true)}
                                                className={`${allTags.length > 0 ? "" : "ml-auto "}inline-flex items-center gap-1 rounded font-sans text-xs font-medium text-ink-muted transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent`}
                                            >
                                                <Plus className="h-3.5 w-3.5" />
                                                Invitation externe
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>

                            {error ? (
                                <p className="p-6 text-center text-sm text-danger">{error}</p>
                            ) : !result && loading ? (
                                <ProspectRowsSkeleton scope={tab} />
                            ) : rows.length === 0 && !loading ? (
                                <p className="p-12 text-center font-sans text-sm text-ink-faint">
                                    {EMPTY_TEXT[tab]}
                                </p>
                            ) : (
                                <ul className={`divide-y divide-border ${loading ? "opacity-60" : ""}`}>
                                    {rows.map((row) => (
                                        <ProspectListRow
                                            key={row.personId}
                                            row={row}
                                            scope={tab}
                                            busy={busyId === row.personId}
                                            allTags={allTags}
                                            checked={checked.has(row.personId)}
                                            onCheck={(v) =>
                                                setChecked((s) => {
                                                    const next = new Set(s);
                                                    if (v) next.add(row.personId);
                                                    else next.delete(row.personId);
                                                    return next;
                                                })
                                            }
                                            h={handlers}
                                        />
                                    ))}
                                </ul>
                            )}

                            {result && result.totalPages > 1 && (
                                <div className="flex items-center justify-between border-t border-border px-4 py-2">
                                    <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} aria-label="Page précédente">
                                        <ChevronLeft className="h-4 w-4" />
                                    </Button>
                                    <span className="font-mono text-xs text-ink-muted">
                                        {result.page} / {result.totalPages}
                                    </span>
                                    <Button variant="ghost" size="sm" disabled={page >= result.totalPages} onClick={() => setPage((p) => p + 1)} aria-label="Page suivante">
                                        <ChevronRight className="h-4 w-4" />
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {openId && (
                <InviteDialog
                    personId={openId}
                    me={me}
                    templates={templates.filter((t) => momentOf(t) === "invitation")}
                    onManage={setManager}
                    onClose={() => setOpenId(null)}
                    onInvite={onInvite}
                    onSigned={async (personId, signed) => {
                        try {
                            const state = await setOutcome(personId, { signed });
                            applyState(personId, state);
                            return state;
                        } catch {
                            toast.error("Impossible de mettre à jour.");
                            return null;
                        }
                    }}
                />
            )}

            {manager && (
                <MessagesManager
                    focus={manager}
                    offer={offer}
                    templates={templates}
                    onOfferSaved={setOffer}
                    onTemplatesChange={setTemplates}
                    onClose={() => setManager(null)}
                />
            )}

            {messageRow && (
                <MessageDialog
                    row={messageRow}
                    me={me}
                    templates={templates}
                    onManage={() => setManager("templates")}
                    onClose={() => setMessageRow(null)}
                    onSent={onMessageSent}
                />
            )}

            {externalOpen && (
                <ExternalInvitesDialog
                    onClose={() => setExternalOpen(false)}
                    onDone={() => {
                        load();
                        refreshOverview();
                    }}
                />
            )}
        </div>
    );
}
